import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getAllModule,
  getRoleById,
  PostAddRole,
  PostUpdateRole,
} from "../../hooks/services/api-services";
import { decryptAEStoJSON, toastEmitter } from "../../utils/utilities";
import { API_RESPONSE } from "../../utils/app-constants";
import { ROUTES } from "../../hooks/routes/routes-constant";
import {
  alphanumericHypenRegex,
  NoLeadingSpaceRegex,
} from "../../utils/regexValidation";
import BreadCrum from "../../components/common/BreadCrum";
import Input from "../../components/common/input";
import { handleFormInput } from "../../utils/form-utils";
import TextArea from "../../components/common/text-area";
import Button from "../../components/common/button";


import {
  BLUEEDIT_ICON,
  BLUEEYE_ICONIMG,
  BLUEPLUSICON_ICON,
  BLUESTATUS,
  GREYEDIT_ICON,
  GREYEYE_ICONIMG,
  GREYPLUSICON_ICON,
  GREYSTATUS,
} from "../../utils/aap-image-constant";
import { useTranslation } from "react-i18next";

const AddRole = () => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { value } = useSelector((state) => state?.loggedUser);
  const userData = decryptAEStoJSON(value);
  const [payload, setPayload] = useState({
    id: 0,
    roleName: "",
    clinicId: userData?.id ?? 0,
    doctorId: 0,
    roleDescription: "",
    moduleRequestList: [],
    allModuleSelected: false,
    languageType: i18n.language
  });

  const handleDataBack = () => {
    setPayload({
      id: 0,
      roleName: "",
      clinicId: userData?.id ?? 0,
      roleDescription: "",
      moduleRequestList: [],
      allModuleSelected: false,
      languageType: i18n.language
    });
    navigate(ROUTES.ROLE_MANAGEMENT);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const checkfromType = location?.state?.formType;
  const roleId = location?.state?.roleId;

  const [modulePermission, setModulePermission] = useState([]);
  const getAdminPermissionModule = async () => {
    try {
      const response = await getAllModule(i18n.language, "clinicModule");
      if (response?.status === 200) {
        setModulePermission(response?.data?.data || []);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    }
  };

  const fetchRoleById = async (roleId, languageType) => {
    try {
      const roleResponse = await getRoleById(roleId, languageType);
      const moduleResponse = await getAllModule(i18n.language, "clinicModule");

      if (roleResponse?.status === 200 && moduleResponse?.status === 200) {
        let roleData = roleResponse?.data?.data || {};
        let allModules = moduleResponse?.data?.data || [];

        const selectedModuleMap = {};
        (roleData?.roleModuleMappingResponseList || []).forEach((mod) => {
          selectedModuleMap[mod?.moduleId] = mod;
        });
        const mergedModulePermissions = allModules?.map((module) => {
          const selected = selectedModuleMap[module?.id] || {}; // Map existing selection

          return {
            id: module.id,
            moduleName: module.moduleName,
            parentModuleName: module.parentModuleName || "",
            moduleCode: module.moduleCode || "",
            moduleAction: selected.moduleAction || 0,
            addAction: selected.addAction || 0,
            updateAction: selected.updateAction || 0,
            deleteAction: selected.deleteAction || 0,
            viewAction: selected.viewAction || 0,
            downloadAction: selected.downloadAction || 0,
            isAllActionSelect: selected.isAllActionSelect || 0,
            status: selected.status || 1,
          };
        });

        setModulePermission(mergedModulePermissions);
        setPayload((prevPayload) => ({
          ...prevPayload,
          id: roleData?.id || roleId,
          roleName: roleData?.roleName || "",
          clinicId: roleData?.clinicId,
          doctorId: roleData?.doctorId,
          roleDescription: roleData?.roleDescription || "",
          moduleRequestList: mergedModulePermissions,
          allModuleSelected: false,
        }));
      } else {
        toastEmitter("error", roleResponse?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
    }
  };

  useEffect(() => {
    getAdminPermissionModule();
  }, []);
  useEffect(() => {
    if (roleId) {
      fetchRoleById(roleId, i18n.language);
    }
  }, [roleId, i18n.language]);
  const handlePermissionChange = (moduleIndex, permissionType, value) => {
    const newPermission = modulePermission?.map((existingPermission, index) => {
      if (moduleIndex === index) {
        const updatedPermission = {
          ...existingPermission,
          [permissionType]: value ? 1 : 0,
        };

        return updatedPermission;
      }
      return existingPermission;
    });

    setModulePermission(newPermission);
  };

  // Ensure Payload Updates after modulePermission Changes
  useEffect(() => {
    setPayload((prevPayload) => ({
      ...prevPayload,
      moduleRequestList: modulePermission, // Ensure modulePermission is updated before setting payload
    }));
  }, [modulePermission]);

  const handleAllActionSelect = (moduleIndex, value) => {
    const newPermission = modulePermission?.map((existingPermission, index) => {
      if (moduleIndex === index) {
        return {
          ...existingPermission,
          addAction: value ? 1 : 0,
          updateAction: value ? 1 : 0,
          viewAction: value ? 1 : 0,
          moduleAction: value ? 1 : 0, // Ensure status is updated
          selectAll: value ? 1 : 0,
        };
      }
      return existingPermission;
    });

    setModulePermission(newPermission);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !NoLeadingSpaceRegex.test(payload?.roleName) ||
      payload?.roleName.trim() === "" ||
      !alphanumericHypenRegex.test(payload?.roleName)
    ) {
      return toastEmitter(
        "error",
        "Please enter a valid Role Name (No leading spaces, cannot be empty, only alphanumeric & hyphen allowed)."
      );
    }

    if (
      !NoLeadingSpaceRegex.test(payload?.roleDescription) ||
      payload?.roleDescription.trim() === ""
    ) {
      return toastEmitter(
        "error",
        "Description is mandatory (No leading spaces, cannot be empty)"
      );
    }

    const selectedModules = payload.moduleRequestList.filter(
      (module) =>
        module.addAction === 1 ||
        module.viewAction === 1 ||
        module.updateAction === 1 ||
        module.moduleAction === 1
    );

    if (selectedModules.length === 0) {
      return toastEmitter(
        "error",
        "At least one module permission is mandatory!"
      );
    }

    const updatedPayload = {
      ...payload,
      moduleRequestList: selectedModules,
    };
    setIsLoading(true);

    try {
      let response;
      if (checkfromType === "add") {
        response = await PostAddRole(updatedPayload);
      } else {
        response = await PostUpdateRole(updatedPayload);
      }

      if (response?.data?.status !== 200) {
        toastEmitter("error", response?.data?.message);
      } else {
        toastEmitter(
          "success",
          `Role ${checkfromType === "add" ? "added" : "updated"} successfully!`
        );
        navigate(ROUTES.ROLE_MANAGEMENT, {
          state: { activeTab: "role" },
        });

      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAllModules = (value) => {
    const updatedPermissions = modulePermission.map((permission) => ({
      ...permission,
      addAction: value ? 1 : 0,
      viewAction: value ? 1 : 0,
      updateAction: value ? 1 : 0,
      deleteAction: value ? 1 : 0, //  Added deleteAction
      downloadAction: value ? 1 : 0, //  Added downloadAction
      moduleAction: value ? 1 : 0,
      selectAll: value ? 1 : 0,
    }));

    setModulePermission(updatedPermissions);

    //  Ensure payload updates after state change
    setPayload((prevPayload) => ({
      ...prevPayload,
      moduleRequestList: updatedPermissions,
    }));
  };

  //  Check if all modules are selected properly
  const isAllModulesSelected = modulePermission.every(
    (permission) =>
      permission.addAction === 1 &&
      permission.viewAction === 1 &&
      permission.updateAction === 1 &&
      permission.moduleAction === 1
  );

  return (
    <div className="main_datatable my-lg-3 mt-1 ">
      <form onSubmit={handleSubmit}>
        <div className=" add_doctor card p-3 border border-radius_input mb-3">
          <BreadCrum
            firstData={t("Role Management")}
            iconshow1={true}
            secondData={`${checkfromType === "add"
              ? t("Add")
              : checkfromType === t("edit")
                ? t("Edit")
                : t("View")
              } ${t("Role")}`}
            onFirstDataClick={handleDataBack}
          />
          <div className="row">
            <div className="col-md-6 mb-2">
              <Input
                className="border-radius_input"
                type="text"
                value={payload.roleName}
                disabled={checkfromType === "view"}
                name="roleName"
                placeHolder={t("Enter role")}
                handleChange={(e) => setPayload(handleFormInput(e, payload))}
                labelName={t("Role Name")}
              />
            </div>

            <div className="col-md-12 ">
              <TextArea
                className="border-radius_input"
                labelName={t("Brief Description")}
                placeholder={t("Enter description")}
                name="roleDescription"
                disabled={checkfromType === "view"}
                handleChange={(e) => setPayload(handleFormInput(e, payload))}
                value={payload.roleDescription}
                showastrict={true}
              />
            </div>

            <div className="col-sm-12">
              <label className="form-label">
                {t("Role Access Setup")} <span className="text-danger">*</span>
              </label>
              <div className="table-responsive">
                <table
                  className="table  rounded-3"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr className="">
                      <th
                        scope="col"
                        className=""
                      >
                        <input
                          type="checkbox"
                          disabled={checkfromType === "view"}
                          className="mr-1 mt-1 "
                          checked={isAllModulesSelected}
                          onChange={(e) =>
                            handleSelectAllModules(e.target.checked)
                          }
                        />
                        <label className="ms-2"> Select All</label>
                      </th>
                      <th scope="col" className="">
                        <label>Permissions</label>
                      </th>
                      <th scope="col" className="">
                        <label>Add</label>
                      </th>
                      <th scope="col" className="">
                        <label>View</label>
                      </th>
                      <th scope="col " className=" fs-14 ">
                        <label>Edit</label>
                      </th>
                      <th scope="col" className="">
                        <label> Status</label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modulePermission?.length > 0 &&
                      modulePermission?.map((permissiondata, index) => {
                        const hitSelectAll = {
                          addAction: permissiondata?.addAction,
                          viewAction: permissiondata?.viewAction,
                          updateAction: permissiondata?.updateAction,
                          moduleAction: permissiondata?.moduleAction,
                        };
                        const checkSelect = Object.values(hitSelectAll).every(
                          (value) => value === 1
                        );

                        return (
                          <>
                            <tr className="">
                              <td className="">
                                <input
                                  id={permissiondata.moduleName + index}
                                  type="checkbox"
                                  checked={
                                    permissiondata.moduleName === "Dashboard" &&
                                      permissiondata?.viewAction === 1
                                      ? true
                                      : checkSelect
                                  }
                                  disabled={checkfromType === "view"}
                                  onChange={(e) =>
                                    handleAllActionSelect(
                                      index,
                                      e.target.checked
                                    )
                                  }
                                />
                              </td>
                              <td className="">
                                {permissiondata.moduleName}
                              </td>
                              <td className="">
                                {permissiondata.moduleName !== "Dashboard" && (
                                  <label>
                                    <input
                                      disabled={checkfromType === "view"}
                                      id={permissiondata.moduleName + index}
                                      type="checkbox"
                                      name={permissiondata.moduleName + index}
                                      className="form-check-input d-none"
                                      checked={permissiondata.addAction === 1}
                                      onChange={(e) =>
                                        handlePermissionChange(
                                          index,
                                          "addAction",
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <img
                                      src={
                                        permissiondata.addAction === 1
                                          ? BLUEPLUSICON_ICON
                                          : GREYPLUSICON_ICON
                                      } // Blue plus icon when active, grey plus when inactive
                                      alt="add-action"
                                      className="roleicons"
                                      style={{
                                        opacity:
                                          checkfromType === "view" ? 0.5 : 1,
                                        pointerEvents:
                                          checkfromType === "view"
                                            ? "none"
                                            : "auto",
                                      }}
                                    />
                                  </label>
                                )}
                              </td>

                              <td className="">
                                <label>
                                  <input
                                    disabled={checkfromType === "view"}
                                    id={permissiondata.moduleName + index}
                                    type="checkbox"
                                    name={permissiondata.moduleName + index}
                                    className="form-check-input d-none"
                                    checked={permissiondata.viewAction === 1}
                                    onChange={(e) =>
                                      handlePermissionChange(
                                        index,
                                        "viewAction",
                                        e.target.checked
                                      )
                                    }
                                  />

                                  <img
                                    src={
                                      permissiondata.viewAction === 1
                                        ? BLUEEYE_ICONIMG
                                        : GREYEYE_ICONIMG
                                    }
                                    alt="view-action"
                                    className="roleicons"
                                    style={{
                                      opacity:
                                        checkfromType === "view" ? 0.5 : 1,
                                      pointerEvents:
                                        checkfromType === "view"
                                          ? "none"
                                          : "auto",
                                    }}
                                  />
                                </label>
                              </td>
                              <td className="">
                                {permissiondata.moduleName !== "Dashboard" && (
                                  <label>
                                    <input
                                      disabled={checkfromType === "view"}
                                      id={permissiondata.moduleName + index}
                                      className="form-check-input  d-none "
                                      type="checkbox"
                                      checked={
                                        permissiondata.updateAction === 1
                                      }
                                      name={permissiondata.moduleName + index}
                                      onChange={(e) =>
                                        handlePermissionChange(
                                          index,
                                          "updateAction",
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <img
                                      src={
                                        permissiondata.updateAction === 1
                                          ? BLUEEDIT_ICON
                                          : GREYEDIT_ICON
                                      }
                                      alt="edit-action"
                                      className="roleicons"
                                      style={{
                                        opacity:
                                          checkfromType === "view" ? 0.5 : 1,
                                        pointerEvents:
                                          checkfromType === "view"
                                            ? "none"
                                            : "auto",
                                      }}
                                    />
                                  </label>
                                )}
                              </td>

                              <td className="">
                                {permissiondata.moduleName !== "Dashboard" && (
                                  <label>
                                    <input
                                      disabled={checkfromType === "view"}
                                      id={permissiondata.moduleName + index}
                                      type="checkbox"
                                      name={permissiondata.moduleName + index}
                                      className="form-check-input d-none"
                                      checked={
                                        permissiondata.moduleAction === 1
                                      }
                                      onChange={(e) =>
                                        handlePermissionChange(
                                          index,
                                          "moduleAction",
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <img
                                      src={
                                        permissiondata.moduleAction === 1
                                          ? BLUESTATUS
                                          : GREYSTATUS
                                      } // Change based on the status
                                      alt="status-action"
                                      className="roleicons"
                                      style={{
                                        opacity:
                                          checkfromType === "view" ? 0.5 : 1,
                                        pointerEvents:
                                          checkfromType === "view"
                                            ? "none"
                                            : "auto",
                                      }}
                                    />
                                  </label>
                                )}
                              </td>
                            </tr>
                          </>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap  justify-content-center ">
          <Button
            type="button"
            className={`px-5 py-2 rounded border-0  cancelbtn mb-2 ${i18n.language === "ar" ? "ms-2" : "me-2"}`}
            label="Cancel"
            onClick={handleDataBack}
          />

          {checkfromType !== "view" && (
            <button
              type="submit"
              className="px-4 py-2 rounded border-0 text-white submitbtn mb-2"
            >
              {isLoading && <i className="fa fa-spinner fa-spin me-2"></i>}
              {checkfromType === "add"
                ? "Add"
                : checkfromType === t("edit")
                  ? "Update"
                  : ""}{" "}
              {t("Role")}
            </button>
          )}
        </div>
      </form>

    </div>
  );
};

export default AddRole;
